import { Button, Flex, Menu, MenuButton, TextField } from '@aws-amplify/ui-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AiOutlineArrowRight } from 'react-icons/ai';
import { BsChevronDown, BsChevronRight, BsLayoutSidebar, BsThreeDots } from 'react-icons/bs';
import ReactModal from 'react-modal';
import { useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { GlobalContext } from '../global-context';
import { SearchHistoryItem } from '../services/search-history.service';
import General from '../utils/GeneralUtils';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';
import ConfirmModal from './ConfirmModal';
import './SearchHistoryPanel.scss';

export interface SearchHistoryPanelProps {
  basePath: string;
  teamShared: SearchHistoryItem[];
  starred: SearchHistoryItem[];
  recent: SearchHistoryItem[];
  currentUserId: string;
  onToggleStar: (id: string) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onShareWithTeam: (id: string) => Promise<void>;
  onUnshareFromTeam: (id: string) => Promise<void>;
  isOpen: boolean;
  onTogglePanel: () => void;
}

export const SearchHistoryPanel: React.FC<SearchHistoryPanelProps> = ({
  teamShared,
  starred,
  recent,
  currentUserId,
  onToggleStar,
  onArchive,
  onRename,
  onShareWithTeam,
  onUnshareFromTeam,
  basePath,
  isOpen,
  onTogglePanel,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const globalContext = useContext(GlobalContext);

  /*
   * Stream is behind an admin-team check while it is being tested. Same gate the sales
   * navigator and the admin routes use (SideNav.tsx, App.tsx), rather than a real feature
   * flag - this is meant to come out, not to be configured.
   */
  const canStream = General.getAdminTeamIds().includes(globalContext.authInfo?.teamId || '');
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<SearchHistoryItem | null>(null);
  const [renameName, setRenameName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<SearchHistoryItem | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    return LocalStorageService.get<Record<string, boolean>>(LocalStorageKeys.SEARCH_HISTORY_COLLAPSED_SECTIONS);
  });

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const newState = { ...prev, [section]: !prev[section] };
      LocalStorageService.set(LocalStorageKeys.SEARCH_HISTORY_COLLAPSED_SECTIONS, newState);
      return newState;
    });
  };

  // Check if a search item is currently active by comparing checksums
  const isItemActive = (search: SearchHistoryItem) => {
    // Compare using checksum - the backend returns checksum with each item
    // and we can pass currentChecksum from the parent
    // For now, compare the q param directly
    const currentQParam = new URLSearchParams(location.search).get('q');
    if (!currentQParam) return false;
    const searchParams = new URLSearchParams(search.query);
    const searchQParam = searchParams.get('q');
    return currentQParam === searchQParam;
  };

  const handleSearchClick = (search: SearchHistoryItem) => {
    const decodedQuery = decodeURIComponent(search.query);
    const url = `${basePath}?${decodedQuery}`;
    navigate(url);
  };

  const handleStarClick = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await onToggleStar(id);
  };

  const handleRenameClick = (e: React.MouseEvent, search: SearchHistoryItem) => {
    e.stopPropagation();
    setRenameTarget(search);
    setRenameName(search.name);
    setRenameModalOpen(true);
  };

  const handleRenameSubmit = async () => {
    if (!renameTarget || !renameName.trim()) return;
    setIsRenaming(true);
    try {
      await onRename(renameTarget.id, renameName.trim());
      setRenameModalOpen(false);
      setRenameTarget(null);
      setRenameName('');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameCancel = () => {
    setRenameModalOpen(false);
    setRenameTarget(null);
    setRenameName('');
  };

  const handleDeleteClick = (e: React.MouseEvent, search: SearchHistoryItem) => {
    e.stopPropagation();
    setArchiveTarget(search);
    setArchiveModalOpen(true);
  };

  // Streams exist only for starred and team-shared searches - starring or sharing is what
  // queues the audience build that resolves their matching contacts.
  const handleStreamClick = (e: React.MouseEvent, search: SearchHistoryItem) => {
    e.stopPropagation();
    navigate(`/directory/people/stream/${search.id}`);
  };

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    setIsArchiving(true);
    try {
      await onArchive(archiveTarget.id);
      setArchiveModalOpen(false);
      setArchiveTarget(null);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleCancelArchive = () => {
    setArchiveModalOpen(false);
    setArchiveTarget(null);
  };

  const handleShareClick = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await onShareWithTeam(id);
  };

  const handleUnshareClick = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await onUnshareFromTeam(id);
  };

  const hasContent = teamShared.length > 0 || starred.length > 0 || recent.length > 0;

  return (
    <>
      <div className={`search-history-panel ${!isOpen ? 'search-history-panel--hidden' : ''}`}>
        <div className="search-history-panel__header">
          <Button
            size="small"
            onClick={onTogglePanel}
            className="nohover close-sidebar-btn"
            title="Hide search history"
          >
            <BsLayoutSidebar size={14} />
          </Button>
        </div>

      <div className="search-history-panel__content">
        {!hasContent ? (
          null
        ) : (
          <>
            {teamShared.length > 0 && (
              <>
                <div
                  className="search-history-panel__section-header search-history-panel__section-header--clickable"
                  onClick={() => toggleSection('teamShared')}
                >
                  {collapsedSections.teamShared ? <BsChevronRight size={10} /> : <BsChevronDown size={10} />}
                  Team Shared
                </div>
                {!collapsedSections.teamShared && (
                  <ul>
                    {teamShared.map((search) => (
                      <SearchItem
                        key={search.id}
                        search={search}
                        section="teamShared"
                        currentUserId={currentUserId}
                        isActive={isItemActive(search)}
                        canStream={canStream}
                        onSearchClick={handleSearchClick}
                        onStarClick={handleStarClick}
                        onRenameClick={handleRenameClick}
                        onArchiveClick={handleDeleteClick}
                        onStreamClick={handleStreamClick}
                        onShareClick={handleShareClick}
                        onUnshareClick={handleUnshareClick}
                      />
                    ))}
                  </ul>
                )}
              </>
            )}

            {starred.length > 0 && (
              <>
                <div
                  className="search-history-panel__section-header search-history-panel__section-header--clickable"
                  onClick={() => toggleSection('starred')}
                >
                  {collapsedSections.starred ? <BsChevronRight size={10} /> : <BsChevronDown size={10} />}
                  My Favorites
                </div>
                {!collapsedSections.starred && (
                  <ul>
                    {starred.map((search) => (
                      <SearchItem
                        key={search.id}
                        search={search}
                        section="starred"
                        currentUserId={currentUserId}
                        isActive={isItemActive(search)}
                        canStream={canStream}
                        onSearchClick={handleSearchClick}
                        onStarClick={handleStarClick}
                        onRenameClick={handleRenameClick}
                        onArchiveClick={handleDeleteClick}
                        onStreamClick={handleStreamClick}
                        onShareClick={handleShareClick}
                        onUnshareClick={handleUnshareClick}
                      />
                    ))}
                  </ul>
                )}
              </>
            )}

            {recent.length > 0 && (
              <>
                <div
                  className="search-history-panel__section-header search-history-panel__section-header--clickable"
                  onClick={() => toggleSection('recent')}
                >
                  {collapsedSections.recent ? <BsChevronRight size={10} /> : <BsChevronDown size={10} />}
                  Recent Searches
                </div>
                {!collapsedSections.recent && (
                  <TransitionGroup component="ul">
                    {recent.map((search) => (
                      <CSSTransition
                        key={search.id}
                        timeout={300}
                        classNames="search-item"
                      >
                        <SearchItem
                          search={search}
                          section="recent"
                          currentUserId={currentUserId}
                          isActive={isItemActive(search)}
                          canStream={canStream}
                          onSearchClick={handleSearchClick}
                          onStarClick={handleStarClick}
                          onRenameClick={handleRenameClick}
                          onArchiveClick={handleDeleteClick}
                          onStreamClick={handleStreamClick}
                          onShareClick={handleShareClick}
                          onUnshareClick={handleUnshareClick}
                        />
                      </CSSTransition>
                    ))}
                  </TransitionGroup>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
    <div
      onClick={onTogglePanel}
      className={`search-history-panel__open-btn ${isOpen ? 'search-history-panel__open-btn--hidden' : ''}`}
      title="Open search history"
    >
      <AiOutlineArrowRight size={14} />
    </div>

    {/* Rename Modal */}
    <ReactModal
      isOpen={renameModalOpen}
      onRequestClose={handleRenameCancel}
      style={General.getModalDefaults({ width: '500px' })}
      closeTimeoutMS={200}
      parentSelector={General.getModalRoot}
    >
      <Flex direction="column" gap={20}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Rename Search</h3>
        <TextField
          label="Name"
          value={renameName}
          onChange={(e) => setRenameName(e.target.value)}
          autoFocus
        />
        <Flex gap={10} justifyContent="flex-end">
          <Button size="small" onClick={handleRenameCancel} disabled={isRenaming}>
            Cancel
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={handleRenameSubmit}
            isLoading={isRenaming}
            loadingText="Saving..."
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </ReactModal>

    {/* Archive Confirmation Modal */}
    <ConfirmModal
      isOpen={archiveModalOpen}
      onClose={handleCancelArchive}
      onConfirm={handleConfirmArchive}
      title="Archive Search"
      message={`Are you sure you want to archive "${archiveTarget?.name}"?`}
      confirmText="Archive"
      cancelText="Cancel"
      isLoading={isArchiving}
      loadingText="Archiving..."
      variant="destructive"
    />
  </>
  );
};

export interface SearchItemProps {
  /**
   * Whether to offer Stream. Restricted to the admin teams while the feature is being
   * tested - audiences only exist for searches that are starred or team shared, and the
   * nightly rebuild is not yet scheduled, so a stream can legitimately have nothing to
   * show. Drop the prop to release it to everyone.
   */
  canStream: boolean;
  search: SearchHistoryItem;
  section: 'teamShared' | 'starred' | 'recent';
  currentUserId: string;
  isActive: boolean;
  onSearchClick: (search: SearchHistoryItem) => void;
  onStarClick: (e: React.MouseEvent, id: string) => void;
  onRenameClick: (e: React.MouseEvent, search: SearchHistoryItem) => void;
  onArchiveClick: (e: React.MouseEvent, search: SearchHistoryItem) => void;
  onStreamClick: (e: React.MouseEvent, search: SearchHistoryItem) => void;
  onShareClick: (e: React.MouseEvent, id: string) => void;
  onUnshareClick: (e: React.MouseEvent, id: string) => void;
}

/**
 * One saved search row, with the context menu that hangs off it.
 *
 * Exported so the boss UI gallery can render it against mock data - the menu is otherwise
 * only reachable by hovering a real saved search in the people directory.
 */
export const SearchItem: React.FC<SearchItemProps> = ({
  search,
  section,
  currentUserId,
  isActive,
  canStream,
  onSearchClick,
  onStarClick,
  onRenameClick,
  onArchiveClick,
  onStreamClick,
  onShareClick,
  onUnshareClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = nameRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [search.name]);

  // Whether current user owns this search (can rename/archive)
  const isOwner = search.userId === currentUserId;
  // Whether this item is already shared
  const isShared = search.isTeamShared;
  // Can unshare only if current user shared it
  const canUnshare = search.sharedByUserId === currentUserId;

  return (
    <div
      className={`search-history-panel__item ${isActive ? 'search-history-panel__item--active' : ''}`}
      onClick={() => onSearchClick(search)}
    >
      <div className="search-history-panel__item-content">
        <div
          ref={nameRef}
          className="search-history-panel__item-name"
          data-tooltip-id={isTruncated ? "main-tooltip" : undefined}
          data-tooltip-content={isTruncated ? search.name : undefined}
          // data-tooltip-delay-show={500}
        >{search.name}</div>
      </div>
      <div
        className={`search-history-panel__item-menu ${isMenuOpen ? 'search-history-panel__item-menu--open' : ''}`}
      >
        <Menu
          size="small"
          menuAlign="end"
          // Opts into the shared context menu styling in src/styles/_context-menu.scss.
          // Amplify puts this on .amplify-menu__content.
          className="context-menu"
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          trigger={
            <MenuButton size="small" className="tiniest nohover">
              <BsThreeDots size={18} />
            </MenuButton>
          }
        >
          {/* Stream - only for curated searches, which are the ones with an audience */}
          {canStream && section !== 'recent' && (
            <Button
              size="small"
              className="menu-item"
              onClick={(e) => {
                setIsMenuOpen(false);
                onStreamClick(e, search);
              }}
            >
              Stream
            </Button>
          )}

          {/* Star/Unstar */}
          <Button
            size="small"
            className="menu-item"
            isDisabled={!isOwner || section === 'teamShared'}
            onClick={(e) => {
              setIsMenuOpen(false);
              onStarClick(e, search.id);
            }}
          >
            {search.isStarred ? 'Unfavorite' : 'Favorite'}
          </Button>

          {/* Share with team - only show if not already shared */}
          {!isShared && (
            <Button
              size="small"
              className="menu-item"
              isDisabled={!isOwner || section === 'teamShared'}
              onClick={(e) => {
                setIsMenuOpen(false);
                onShareClick(e, search.id);
              }}
            >
              Share with team
            </Button>
          )}

          {/* Unshare from team - only show if shared */}
          {isShared && (
            <Button
              size="small"
              className="menu-item"
              isDisabled={!canUnshare}
              onClick={(e) => {
                setIsMenuOpen(false);
                onUnshareClick(e, search.id);
              }}
            >
              Unshare from team
            </Button>
          )}

          {/* Rename */}
          <Button
            size="small"
            className="menu-item"
            isDisabled={!isOwner}
            onClick={(e) => {
              setIsMenuOpen(false);
              onRenameClick(e, search);
            }}
          >
            Rename
          </Button>

          {/* Archive */}
          <Button
            size="small"
            className="menu-item"
            isDisabled={!isOwner}
            onClick={(e) => {
              setIsMenuOpen(false);
              onArchiveClick(e, search);
            }}
          >
            Archive
          </Button>
        </Menu>
      </div>
    </div>
  );
};
